import React, { useState, useEffect, useMemo } from 'react';
import { Search, Sparkles, ChevronDown, LifeBuoy, CheckCircle, Shield, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import EmptyState from '../ui/EmptyState';
// Custom Alert Component
const Alert = ({ children }) => (
  <div className="rounded-lg border border-primary-100 bg-primary-50 px-4 py-3 text-sm text-primary-800">
    {children}
  </div>
);

const categories = [
  {
    id: 'platform',
    title: 'Platform Features',
    icon: Sparkles,
    description: 'Core functionalities and capabilities'
  },
  {
    id: 'security',
    title: 'Security & Compliance',
    icon: Shield,
    description: 'Data protection and regulatory compliance'
  },
  {
    id: 'implementation',
    title: 'Implementation',
    icon: CheckCircle,
    description: 'Setup, integration, and deployment'
  },
  {
    id: 'support',
    title: 'Support & Training',
    icon: LifeBuoy,
    description: '24/7 assistance and resources'
  }
];

// Same FAQ data structure as before
const faqs = {
  platform: [
    {
      question: "What is FlexCOA's eCOA platform?",
      answer: "FlexCOA is a comprehensive electronic Clinical Outcome Assessment (eCOA) platform designed to modernize clinical trials. Our solution includes:",
      details: [
        "Web and mobile interfaces for patient-reported outcomes (PRO)",
        "Clinician-reported outcomes (ClinRO) with advanced workflow management",
        "Observer-reported outcomes (ObsRO) with real-time data capture",
        "Performance outcome assessments (PerfO) with multimedia capabilities",
        "Offline data collection with automatic synchronization"
      ]
    },
    {
      question: "What devices and platforms are supported?",
      answer: "FlexCOA supports a wide range of devices and platforms to ensure maximum accessibility:",
      details: [
        "iOS and Android mobile applications",
        "Web-based interface for desktop and tablet access",
        "BYOD (Bring Your Own Device) support",
        "Provisioned device management",
        "Cross-platform data synchronization"
      ]
    }
  ],
  security: [
    {
      question: "How does FlexCOA ensure data security and privacy?",
      answer: "We implement comprehensive security measures including:",
      details: [
        "End-to-end encryption for all data transmission",
        "HIPAA, GDPR, and CCPA compliance",
        "Regular third-party security audits",
        "Role-based access control (RBAC)",
        "Audit trails and activity logging"
      ]
    }
  ],
  implementation: [
    {
      question: "What is the typical implementation timeline?",
      answer: "Implementation timelines vary based on study complexity, but typically include:",
      details: [
        "Initial setup and configuration: 2-4 weeks",
        "Integration with existing systems: 1-2 weeks",
        "Validation and testing: 2-3 weeks",
        "User training and documentation: 1-2 weeks",
        "Go-live support and monitoring: Ongoing"
      ]
    }
  ],
  support: [
    {
      question: "What support options are available?",
      answer: "If you are taking part in a study, contact your study team first. They know your study and can help with your schedule, your surveys and signing in.",
      details: [
        "Participants and caregivers: contact your study coordinator or the staff at your study site",
        "Study teams: email support@flexcoa.com"
      ]
    }
  ]
};

const FAQItem = ({ item, isOpen, onToggle }) => {
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={onToggle}
        className="group flex w-full items-start justify-between gap-6 rounded-lg py-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 focus-visible:ring-offset-2"
      >
        <span className="text-base font-semibold text-gray-900 transition-colors group-hover:text-primary-700 sm:text-lg">
          {item.question}
        </span>
        <ChevronDown
          className={`mt-0.5 h-5 w-5 flex-shrink-0 transition-[transform,color] duration-200 ${
            isOpen ? 'rotate-180 text-primary-600' : 'text-gray-400 group-hover:text-primary-600'
          }`}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="pb-5">
              <p className="mb-3 text-[15px] leading-relaxed text-gray-600">{item.answer}</p>
              {item.details && (
                <ul className="space-y-2">
                  {item.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="mr-3 mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600" />
                      <span className="text-[15px] text-gray-600">{detail}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Content sits at the top of each card, so cards with a shorter description
// line up with their neighbours in the row.
const CategoryCard = ({ category, isActive, onClick }) => (
  <button
    onClick={onClick}
    aria-pressed={isActive}
    className={`flex h-full w-full flex-col items-start justify-start rounded-xl border p-5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 focus-visible:ring-offset-2 sm:p-6 ${
      isActive
        ? 'border-primary-700 bg-primary-700 text-white shadow-float'
        : 'border-gray-200 bg-white shadow-card hover:border-primary-300'
    }`}
  >
    <category.icon className={`mb-4 h-6 w-6 ${isActive ? 'text-white' : 'text-primary-600'}`} />
    <h3 className={`mb-1.5 text-lg font-semibold ${isActive ? 'text-white' : 'text-gray-900'}`}>
      {category.title}
    </h3>
    <p className={`text-sm leading-relaxed ${isActive ? 'text-white/90' : 'text-gray-600'}`}>
      {category.description}
    </p>
  </button>
);

const ContactSection = () => (
  <div className="mt-10">
    <div className="rounded-xl bg-primary-600 p-6 text-white sm:p-8">
      <h3 className="mb-2 text-xl font-semibold">Need help?</h3>
      <p className="mb-4 text-[15px] text-white/90">
        If you are taking part in a study, contact your study team first.
      </p>
      <div className="flex items-center">
        <Mail className="mr-2.5 h-[18px] w-[18px] flex-shrink-0 text-white/90" />
        <span>support@flexcoa.com</span>
      </div>
    </div>
  </div>
);

const FAQPage = () => {
  const [activeCategory, setActiveCategory] = useState('platform');
  const [searchTerm, setSearchTerm] = useState('');
  const [openItems, setOpenItems] = useState(new Set());
  const [searchResults, setSearchResults] = useState([]);

  const allFaqs = useMemo(() => {
    return Object.entries(faqs).flatMap(([category, items]) =>
      items.map(item => ({ ...item, category }))
    );
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const results = allFaqs.filter(faq =>
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.details?.some(detail => 
          detail.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, allFaqs]);

  const toggleItem = (index) => {
    setOpenItems(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const currentFaqs = searchTerm ? searchResults : faqs[activeCategory] || [];

  return (
    <div>
      <div className="rounded-xl bg-primary-600 px-4 pb-32 pt-14 sm:pt-20">
        <div className="text-center">
          <motion.h1 
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="mb-6 text-3xl font-semibold tracking-tight text-white sm:text-4xl md:text-5xl"
          >
            How can we help you?
          </motion.h1>
          <motion.div 
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="max-w-2xl mx-auto"
          >
            <div className="relative">
              <input
                type="text"
                placeholder="Search for answers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-12 w-full rounded-lg border border-white/60 bg-white/10 pl-11 pr-4 text-base text-white transition-colors placeholder:text-white/75 hover:border-white/80 focus:border-white focus:outline-none focus:ring-2 focus:ring-white/40"
              />
              <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/80" size={18} />
            </div>
          </motion.div>
        </div>
      </div>

      <div className="-mt-24 px-4 sm:px-6">
        <div className="mb-10 grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-4">
          {categories.map((category) => (
            <CategoryCard
              key={category.id}
              category={category}
              isActive={activeCategory === category.id && !searchTerm}
              onClick={() => {
                setActiveCategory(category.id);
                setSearchTerm('');
              }}
            />
          ))}
        </div>

        <div className="card px-5 py-3 sm:px-8 sm:py-4">
          {searchTerm && (
            <div className="mb-2 mt-3">
              <Alert>
                Found {searchResults.length} results for "{searchTerm}"
              </Alert>
            </div>
          )}

          <div>
            {currentFaqs.map((faq, index) => (
              <FAQItem
                key={index}
                item={faq}
                isOpen={openItems.has(index)}
                onToggle={() => toggleItem(index)}
              />
            ))}
          </div>

          {currentFaqs.length === 0 && (
            <EmptyState
              icon={LifeBuoy}
              title="No matching questions found"
              description="Try adjusting your search terms or browse by category"
            />
          )}
        </div>

        <ContactSection />
      </div>
    </div>
  );
};

export default FAQPage;